// src/pages/Users.tsx - Enhanced for Phase 7 OrgAdmin User Invitation
// Adds "Invite User" functionality for OrgAdmins using existing infrastructure
// ✅ UPDATED: Added Bulk Upload functionality

import React, { useState } from 'react';
import { 
  User,
  UserPlus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Shield,
  Mail,
  Phone,
  Badge,
  Calendar,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Eye,
  FileText,
  Upload
} from 'lucide-react';
import { useUserManagement } from '../hooks/useUserManagement';
// Import useAuth from our new AuthContext
import { useAuth } from '../contexts/AuthContext';
import { User as UserType, EmploymentStatus } from '../types/user';
import UserFormModal from '../components/users/UserFormModal';
import OfficerEquipmentModal from '../components/users/OfficerEquipmentModal';
import BulkUploadModal from '../components/users/BulkUploadModal';
// 🆕 Phase 7: Reuse existing InviteAdminModal for OrgAdmin invitations
// Removed InviteAdminModal import - using dedicated System Access page
import { userAPI } from '../services/userAPI';

// Employment status styling
const getStatusColor = (status: EmploymentStatus): string => {
  switch (status) {
    case 'Active': return 'green';
    case 'Inactive': return 'yellow';
    case 'Terminated': return 'red';
    case 'Retired': return 'blue';
    case 'Leave of Absence': return 'purple';
    default: return 'gray';
  }
};

const getStatusIcon = (status: EmploymentStatus) => {
  switch (status) {
    case 'Active': return CheckCircle;
    case 'Inactive': return AlertTriangle;
    case 'Terminated': return XCircle;
    case 'Retired': return Shield;
    case 'Leave of Absence': return Calendar;
    default: return User;
  }
};

// User row component with system access indicator
const UserRow: React.FC<{
  user: UserType;
  isSelected: boolean;
  onSelect: (userId: string, selected: boolean) => void;
  onEdit: (user: UserType) => void;
  onDelete: (user: UserType) => void;
  onViewDetails: (user: UserType) => void;
}> = ({ user, isSelected, onSelect, onEdit, onDelete, onViewDetails }) => {
  const StatusIcon = getStatusIcon(user.employmentStatus);
  const statusColor = getStatusColor(user.employmentStatus);
  
  // System access management moved to dedicated System Access page
  
  return (
    <tr className={`border-b border-gray-200 hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
      {/* Selection checkbox */}
      <td className="px-6 py-4">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => onSelect(user.userId, e.target.checked)}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      </td>
      
      {/* Officer Info */}
      <td className="px-6 py-4">
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            {user.photoUrl ? (
              <img 
                className="h-10 w-10 rounded-full object-cover" 
                src={user.photoUrl} 
                alt={`${user.firstName} ${user.lastName}`}
              />
            ) : (
              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                <User className="h-5 w-5 text-gray-600" />
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <p className="text-sm font-medium text-gray-900">
                {user.firstName} {user.lastName}
              </p>
             {/* System access indicators removed - see System Access page */}
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <Badge className="h-3 w-3" />
              <span>#{user.badgeNumber}</span>
              <span>•</span>
              <span>#{user.employeeNumber}</span>
            </div>
          </div>
        </div>
      </td>
      
      {/* Rank & Assignment */}
      <td className="px-6 py-4">
        <p className="text-sm font-medium text-gray-900">{user.rank}</p>
        <p className="text-sm text-gray-500">{user.assignment}</p>
      </td>
      
      {/* Contact Info */}
      <td className="px-6 py-4">
        <div className="space-y-1">
          <div className="flex items-center space-x-1 text-sm text-gray-900">
            <Mail className="h-3 w-3" />
            <span>{user.email}</span>
          </div>
          {user.phone && (
            <div className="flex items-center space-x-1 text-sm text-gray-500">
              <Phone className="h-3 w-3" />
              <span>{user.phone}</span>
            </div>
          )}
        </div>
      </td>
      
      {/* Status */}
      <td className="px-6 py-4">
        <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium bg-${statusColor}-100 text-${statusColor}-800`}>
          <StatusIcon className="h-3 w-3" />
          <span>{user.employmentStatus}</span>
        </div>
      </td>
      
      {/* Hire Date */}
      <td className="px-6 py-4 text-sm text-gray-500">
        {new Date(user.hireDate).toLocaleDateString()}
      </td>
      
      {/* Actions */}
      <td className="px-6 py-4">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onViewDetails(user)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="View Details"
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            onClick={() => onEdit(user)}
            className="text-gray-400 hover:text-blue-600 transition-colors"
            title="Edit User"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDelete(user)}
            className="text-gray-400 hover:text-red-600 transition-colors"
            title="Delete User"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};

// Main Users component with Phase 7 enhancements
const Users: React.FC = () => {
  // Get tenantId from AuthContext instead of localStorage
  const { tenantId } = useAuth();
  
  const {
    users,
    loading,
    error,
    totalPages,
    currentPage,
    selectedUsers,
    userStats,
    loadUsers,
    setPage,
    setSearchTerm,
    clearError,
    setSelectedUsers,
    bulkUpdateStatus
  } = useUserManagement();

  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [bulkAction, setBulkAction] = useState('');

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [bulkUploadModalOpen, setBulkUploadModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);

  // Handle search
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setSearchTerm(query);
  };

  // Handle selection
  const handleSelectUser = (userId: string, selected: boolean) => {
    if (selected) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    }
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedUsers(users.map(user => user.userId));
    } else {
      setSelectedUsers([]);
    }
  };

  // Handle bulk actions
  const handleBulkAction = async () => {
    if (!bulkAction || selectedUsers.length === 0) return;
    
    try {
      await bulkUpdateStatus(selectedUsers, bulkAction, `Bulk status update to ${bulkAction}`);
      setBulkAction('');
      alert(`Successfully updated ${selectedUsers.length} users to ${bulkAction}`);
    } catch (err) {
      alert(`Failed to update users: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  // Modal handlers
  const handleCreateUser = () => {
    setSelectedUser(null);
    setCreateModalOpen(true);
  };

  const handleBulkUpload = () => {
    setBulkUploadModalOpen(true);
  };

 // Invite functionality moved to System Access page

  const handleEditUser = (user: UserType) => {
    setSelectedUser(user);
    setEditModalOpen(true);
  };

  const handleDeleteUser = (user: UserType) => {
    if (window.confirm(`Are you sure you want to delete ${user.firstName} ${user.lastName}?`)) {
      // TODO: Implement actual delete functionality
      alert('Delete functionality would be implemented here');
    }
  };

  const handleViewDetails = (user: UserType) => {
    setSelectedUser(user);
    setDetailsModalOpen(true);
  };

  // Modal submit handlers
  const handleUserCreate = async (userData: any) => {
    try {
      console.log('Creating user:', userData);
      const user = await userAPI.createUser(userData);
      console.log('User created successfully:', user);
      
      await loadUsers();
      setCreateModalOpen(false);
      alert('User created successfully!');
    } catch (error) {
      console.error('Error creating user:', error);
      alert(`Failed to create user: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleUserUpdate = async (userData: any) => {
    if (!selectedUser) return;
    
    try {
      console.log('Updating user:', userData);
      const user = await userAPI.updateUser(selectedUser.userId, userData);
      console.log('User updated successfully:', user);
      
      await loadUsers();
      setEditModalOpen(false);
      setSelectedUser(null);
      alert('User updated successfully!');
    } catch (error) {
      console.error('Error updating user:', error);
      alert(`Failed to update user: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleBulkUploadComplete = (result: any) => {
    console.log('Bulk upload complete:', result);
    loadUsers(); // Refresh user list
    if (result.successCount > 0) {
      alert(`Successfully imported ${result.successCount} users!`);
    }
  };

  // Invitation handlers removed - using dedicated System Access page

  const allSelected = users.length > 0 && selectedUsers.length === users.length;
  const someSelected = selectedUsers.length > 0 && selectedUsers.length < users.length;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
              <p className="mt-2 text-gray-600">
                Manage department personnel and user accounts
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={handleBulkUpload}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center space-x-2"
              >
                <Upload className="h-4 w-4" />
                <span>Bulk Upload</span>
              </button>
              <button
                onClick={handleCreateUser}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center space-x-2"
              >
                <UserPlus className="h-4 w-4" />
                <span>Add User</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <User className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Users</p>
                <p className="text-2xl font-bold text-gray-900">{userStats.totalUsers}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Active</p>
                <p className="text-2xl font-bold text-gray-900">{userStats.activeUsers}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Inactive</p>
                <p className="text-2xl font-bold text-gray-900">{userStats.inactiveUsers}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <RefreshCw className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Recent</p>
                <p className="text-2xl font-bold text-gray-900">
                  {users.filter(u => {
                    const weekAgo = new Date();
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    return new Date(u.createdAt) > weekAgo;
                  }).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                <span className="text-sm text-red-800">{error}</span>
              </div>
              <button
                onClick={clearError}
                className="text-red-500 hover:text-red-700"
              >
                <XCircle className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="bg-white rounded-lg shadow-sm border mb-6">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              {/* Search */}
              <div className="flex-1 max-w-lg">
                <div className="relative">
                  <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search users by name, badge, email..."
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Bulk Actions */}
              {selectedUsers.length > 0 && (
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">
                    {selectedUsers.length} selected
                  </span>
                  <select
                    value={bulkAction}
                    onChange={(e) => setBulkAction(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                  >
                    <option value="">Bulk Actions</option>
                    <option value="Active">Set Active</option>
                    <option value="Inactive">Set Inactive</option>
                    <option value="Terminated">Set Terminated</option>
                  </select>
                  <button
                    onClick={handleBulkAction}
                    disabled={!bulkAction}
                    className="bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-300 text-sm"
                  >
                    Apply
                  </button>
                </div>
              )}

              {/* Refresh */}
              <button
                onClick={() => loadUsers()}
                disabled={loading}
                className="text-gray-600 hover:text-gray-800 p-2"
                title="Refresh"
              >
                <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-blue-600 mr-2" />
              <span className="text-gray-600">Loading users...</span>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Users Found</h3>
              <p className="text-gray-600 mb-6">
                {searchQuery ? 'No users match your search criteria.' : 'Get started by adding your first user.'}
              </p>
              {!searchQuery && (
                <div className="flex items-center justify-center space-x-3">
                  <button
                    onClick={handleCreateUser}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                  >
                    Add First User
                  </button>
                  <button
                    onClick={handleBulkUpload}
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                  >
                    Bulk Upload Users
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(input) => {
                          if (input) input.indeterminate = someSelected;
                        }}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Officer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rank & Assignment
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hire Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <UserRow
                      key={user.userId}
                      user={user}
                      isSelected={selectedUsers.includes(user.userId)}
                      onSelect={handleSelectUser}
                      onEdit={handleEditUser}
                      onDelete={handleDeleteUser}
                      onViewDetails={handleViewDetails}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing page {currentPage} of {totalPages}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPage(currentPage - 1)}
                  disabled={currentPage === 1 || loading}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(currentPage - 2 + i, totalPages - 4 + i));
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      disabled={loading}
                      className={`px-3 py-1 text-sm border rounded-md ${
                        pageNum === currentPage
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 hover:bg-gray-50'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => setPage(currentPage + 1)}
                  disabled={currentPage === totalPages || loading}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer Info */}
        <div className="mt-8 text-center text-sm text-gray-500">
           <p>
            User Management System - Department personnel management.
            {users.length > 0 && ` Currently showing ${users.length} users.`}
          </p>
        </div>

      </div>

      {/* Modals */}
      
      {/* Create User Modal - No longer needs operatingTenantId prop */}
      <UserFormModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={handleUserCreate}
        mode="create"
      />

      {/* Edit User Modal - No longer needs operatingTenantId prop */}
      <UserFormModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedUser(null);
        }}
        onSubmit={handleUserUpdate}
        user={selectedUser || undefined}
        mode="edit"
      />

      {/* Officer Equipment/Details Modal */}
      <OfficerEquipmentModal
        isOpen={detailsModalOpen}
        officer={selectedUser}
        onClose={() => {
          setDetailsModalOpen(false);
          setSelectedUser(null);
        }}
        onEquipmentUpdated={loadUsers}
      />

      {/* Bulk Upload Modal */}
      <BulkUploadModal
        isOpen={bulkUploadModalOpen}
        onClose={() => setBulkUploadModalOpen(false)}
        onUploadComplete={handleBulkUploadComplete}
      />

     {/* Invite functionality moved to dedicated System Access page */}
    </div>
  );
};

export default Users;
